/**
 * LoadingManager — Centralized Loading Orchestration Service
 *
 * This service is the single source of truth for ALL loading state mutations.
 * Components and interceptors should call this service rather than committing
 * to Vuex directly. Vuex state is exposed for reactivity only.
 *
 * Supported loading types:
 *   'progress'    → Top progress bar (API calls, navigation)
 *   'fullscreen'  → Full-screen AppLoader overlay
 *   'background'  → Silent, no visual indicator
 *   'silent'      → Alias for background
 *
 * Enterprise messages for AppLoader:
 *   Contextual, concise, professional.
 *   No percentages, no rotating logos, no dots.
 */

import store from '@/store';

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

let _store = null;

/**
 * Lazily resolve the store to avoid circular import issues at module init time.
 */
function getStore() {
  if (!_store) {
    _store = store;
  }
  return _store;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

const LoadingManager = {
  /**
   * Start a loading operation.
   * @param {string} type - 'progress' | 'fullscreen' | 'background' | 'silent'
   */
  start(type = 'progress') {
    getStore().commit('loading/increment', type);
  },

  /**
   * End a loading operation.
   * @param {string} type - must match the type passed to start()
   */
  end(type = 'progress') {
    getStore().commit('loading/decrement', type);
  },

  /**
   * Reset all counters for a given type (or all types).
   * Use after navigation errors or hard resets.
   * @param {string|undefined} type
   */
  reset(type) {
    getStore().commit('loading/reset', type);
  },

  /**
   * Show the full-screen AppLoader with a contextual enterprise message.
   * @param {string} message - Short, professional message. No "Please Wait".
   */
  showAppLoader(message = 'Loading workspace...') {
    getStore().commit('loading/showAppLoader', message);
  },

  /**
   * Hide the full-screen AppLoader.
   */
  hideAppLoader() {
    getStore().commit('loading/hideAppLoader');
  },

  // ─── Convenience: Router Hooks ──────────────────────────────────────────

  /**
   * Call from router.beforeEach to start route-level progress.
   * Does NOT show the full-screen loader — use showAppLoader() for that.
   */
  onRouteStart() {
    getStore().commit('loading/increment', 'progress');
  },

  /**
   * Call from router.afterEach / onError to end route-level progress.
   */
  onRouteEnd() {
    getStore().commit('loading/decrement', 'progress');
  },

  // ─── Convenience: Upload Tracking ───────────────────────────────────────

  /**
   * Begin tracking an upload (background type — uploads have their own UI).
   */
  startUpload() {
    getStore().commit('loading/increment', 'background');
  },

  /**
   * End upload tracking.
   */
  endUpload() {
    getStore().commit('loading/decrement', 'background');
  },

  // ─── Convenience: State Readers (avoid direct Vuex access in services) ──

  get isLoading() {
    return getStore().getters['loading/isLoading'];
  },

  get isAppLoading() {
    return getStore().getters['loading/isAppLoading'];
  },
};

export default LoadingManager;
